import {ProviderLogo} from '@github-ui/copilot-byok/providers/ProviderLogo'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useLocation} from '@github-ui/react-router'
import {CopilotIcon, SyncIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, ButtonGroup, ConfirmationDialog, IconButton, Link} from '@primer/react'
import {clsx} from 'clsx'
import React, {type ReactNode, useCallback, useEffect, useRef, useState} from 'react'

import {useOverlayDirection} from '../contexts/OverlayDirectionContext'
import {
  ENABLE_ADDITIONAL_REQUESTS_URL,
  FREE_QUOTA_TRIGGER,
  MANAGE_BILLING_URL,
  PREMIUM_QUOTA_TRIGGER,
  UPGRADE_PLAN_URL,
} from '../utils/copilot-chat-helpers'
import {getActiveMessages} from '../utils/copilot-chat-subthreading-helpers'
import {
  type CopilotChatMessage,
  type CopilotChatMode,
  type CopilotChatModel,
  type CopilotChatReference,
  CopilotLicenseType,
  CopilotPlan,
} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {copilotLocalStorage} from '../utils/copilot-local-storage'
// eslint-disable-next-line no-restricted-imports
import {useChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {categorizeModels, type CustomModelGroup, hasModelCategoryDiversity} from '../utils/model-picker-helpers'
import {BASE_MODEL_ID, getDefaultModel, isCustomModel} from '../utils/models'
import styles from './ModelPicker.module.css'
import {COPILOT_CHAT_MENU_PORTAL_ROOT} from './PortalContainerUtils'
import {useEntitlement} from './quota/EntitlementContext'

/**
 * Formats a billing multiplier for display.
 * @param multiplier - The billing multiplier value
 * @returns A formatted string like "3x", "1.5x", "0.33x", or "0x" (default)
 */
function formatMultiplier(multiplier: number | undefined): string {
  // Default to 0 if no multiplier is defined
  const value = multiplier ?? 0

  // Convert to number to remove trailing zeros, then back to string
  return `${Number(value.toFixed(2))}x`
}

type ModelPickerType = 'global' | 'message-retry'
type Command = NewConversationDueToExternalContent | NewConversationDueToDegradedImageSupport

// This isn't 100% typesafe, but if this pattern continues to extend considering separating out command types into the shape of their actions and reasons as their own enums.
type NewConversationDueToExternalContent = {
  action: 'requires-new-conversation'
  reason: 'external-content'
}
type NewConversationDueToDegradedImageSupport = {
  action: 'requires-new-conversation'
  reason: 'degraded-image-support'
}

interface ModelPickerProps {
  onNewThreadSelected: () => Promise<void>
  type?: ModelPickerType
  selectedModel?: CopilotChatModel // defaults to the thread's model if unset
  onModelSelected?: (model: CopilotChatModel) => void
  disabled?: boolean
}

export function ModelPicker({
  onNewThreadSelected,
  type = 'global',
  selectedModel,
  onModelSelected,
  disabled = false,
}: ModelPickerProps) {
  const manager = useChatManager()

  const {
    model,
    availableModels,
    mode,
    messages,
    currentReferences,
    selectedThreadID,
    modelsLoading,
    isWaitingOnCopilot,
    streamingMessage,
  } = useChatState()
  const {premiumInteractionsQuotaExceeded, overagesEnabled, plan, licenseType} = useEntitlement()
  const modelsPending = modelsLoading.state === 'pending'
  const {search, pathname} = useLocation()
  const hasSelectedDefaultModel = useRef(false)

  const shouldShowFallbackModel =
    copilotFeatureFlags.premiumRequestQuotasEnabled && premiumInteractionsQuotaExceeded && !overagesEnabled

  let isDisabled = disabled
  // Check FF for disabling model picker while streaming
  if (copilotFeatureFlags.disableModelPickerWhileStreaming) {
    // Disable model picker when streaming is active
    const isStreaming = isWaitingOnCopilot || streamingMessage !== null
    isDisabled = disabled || isStreaming
  }

  const [pending, setPending] = useState<{
    model: CopilotChatModel
    command: Command
  } | null>(null)

  const modelsLoadedRef = useRef(false)

  if (!selectedModel) {
    selectedModel = model
  }

  useEffect(() => {
    if (modelsPending && licenseType !== CopilotLicenseType.Unlicensed) {
      void manager.fetchModels()
    }
  }, [manager, modelsPending, licenseType])

  useEffect(() => {
    // After models have loaded, select the right model. This is either (in order):
    // 1. the model last used for the thread, stored in local storage
    // 2. the default model if no model is stored in local storage
    // 3. the fallback model if none of the prior models are available because
    //    - they are premium models _and_
    //    - the user has exhausted their premium consumption limits _and_
    //    - the user has no additional premium request billing budget enabled
    if (!hasSelectedDefaultModel.current && !modelsPending && availableModels.length > 1) {
      const storedModel = copilotLocalStorage.getModel(selectedThreadID)
      const storedModelId = storedModel?.id

      // check if the thread's stored model is available
      let modelChoice =
        availableModels.find(
          m => m.id === storedModelId && (shouldShowFallbackModel || storedModelId !== BASE_MODEL_ID),
        ) || null
      if (
        // select the default model if the current model is not available
        (!modelChoice && plan) ||
        (plan !== CopilotPlan.IndividualFree &&
          modelChoice?.billing?.is_premium &&
          premiumInteractionsQuotaExceeded &&
          !overagesEnabled)
      ) {
        modelChoice = getDefaultModel(availableModels, plan, premiumInteractionsQuotaExceeded, overagesEnabled)
      }
      if (modelChoice) {
        const isNewThread = selectedThreadID === null
        manager.selectModel(modelChoice, true, isNewThread)
        hasSelectedDefaultModel.current = true
      }
    }
  }, [
    availableModels,
    manager,
    model,
    modelsPending,
    overagesEnabled,
    plan,
    premiumInteractionsQuotaExceeded,
    selectedThreadID,
    shouldShowFallbackModel,
  ])

  // Load model from URL if it exists
  useEffect(() => {
    if (mode !== 'immersive') return

    const urlSearchParams = new URLSearchParams(search)
    const queryModel = urlSearchParams.get('model')

    if (queryModel && (pathname === '/copilot' || pathname === '/copilot/')) {
      if (model.id === queryModel) return

      // Only run this logic when availableModels is populated for the first time
      if (modelsLoadedRef.current || !availableModels?.length) return

      modelsLoadedRef.current = true
      const availableModel = availableModels?.find(m => m.id === queryModel)
      if (availableModel) {
        void manager.selectModel(availableModel, false)
        sendEvent('dotcom_chat.activate', {target: 'COPILOT_MODEL_LOADED_FROM_URL', mode: 'immersive'})
      }
    }
  }, [availableModels, manager, mode, model?.id, pathname, search])

  function selectModel(m: CopilotChatModel): void {
    if (onModelSelected) {
      onModelSelected(m)
    } else {
      void manager.selectModel(m)
    }
  }

  // If you don't have at least two models, don't show the picker
  if (availableModels.length < 2) return null

  const onModelPicked = (newModel: CopilotChatModel) => {
    if (type === 'global' && isSameModel(selectedModel, newModel)) return

    sendEvent('dotcom_chat.activate', {
      target: 'MODEL_PICKER_MODEL_SELECT',
      mode: 'immersive',
      model: newModel.id,
      isUnchanged: isSameModel(selectedModel, newModel),
      type,
    })

    if (isSameModel(selectedModel, newModel)) {
      selectModel(newModel)
    } else if (
      modelSwitchingRequiresNewConversationDueToExternalContent(messages, newModel, type === 'message-retry')
    ) {
      setPending({model: newModel, command: {action: 'requires-new-conversation', reason: 'external-content'}})
    } else if (
      modelSwitchingRequiresNewConversationDueToDegradedImageSupport(
        messages,
        currentReferences,
        newModel,
        type === 'message-retry',
      )
    ) {
      setPending({model: newModel, command: {action: 'requires-new-conversation', reason: 'degraded-image-support'}})
    } else {
      selectModel(newModel)
    }
  }

  return (
    <>
      <ModelPickerDesign
        selectedModel={selectedModel}
        models={availableModels}
        type={type}
        onModelPicked={onModelPicked}
        disabled={isDisabled}
        variant={'invisible'}
        plan={plan ?? undefined}
        mode={mode}
      />
      {pending?.command.action === 'requires-new-conversation' && (
        <NewConversationConfirmationDialog
          model={pending.model}
          reason={pending.command.reason}
          onDismiss={() => {
            sendEvent('dotcom_chat.activate', {
              target: 'MODEL_PICKER_DIALOG_REQUIRES_NEW_CONVERSATION_CANCEL',
              mode: 'immersive',
              model: pending.model.id,
              type,
            })
            setPending(null)
          }}
          onSwitch={async () => {
            sendEvent('dotcom_chat.activate', {
              target: 'MODEL_PICKER_DIALOG_REQUIRES_NEW_CONVERSATION_CONFIRM',
              mode: 'immersive',
              model: pending.model.id,
              type,
            })
            setPending(null)
            await onNewThreadSelected()
            void manager.selectModel(pending.model)
          }}
        />
      )}
    </>
  )
}

interface ModelPickerDesignProps {
  selectedModel: CopilotChatModel
  models: CopilotChatModel[]
  onModelPicked: (m: CopilotChatModel) => void
  type: ModelPickerType
  disabled: boolean
  variant?: 'default' | 'invisible'
  plan?: CopilotPlan
  showCategories?: boolean
  showMultipliers?: boolean
  showPreviewModelsSeparately?: boolean
  /** Chat mode for portal rendering - defaults to 'immersive' */
  mode?: CopilotChatMode
}

interface RetryButtonGroupProps {
  selectedModel: CopilotChatModel
  onModelPicked: (m: CopilotChatModel) => void
  disabled: boolean
  ref?: React.Ref<HTMLDivElement>
}

function RetryButtonGroup({ref, ...props}: RetryButtonGroupProps) {
  const {selectedModel, onModelPicked, disabled, ...buttonProps} = props
  return (
    <ButtonGroup className={styles.messageRetryButtonGroup} ref={ref}>
      <IconButton
        variant="invisible"
        aria-label={`Retry with ${selectedModel.displayName}`}
        onClick={() => onModelPicked(selectedModel)}
        icon={SyncIcon}
        disabled={disabled}
        className={styles.messageRetryButton}
      />
      <div className={styles.messageRetryDivider} />
      <IconButton
        variant="invisible"
        icon={TriangleDownIcon}
        aria-label="Retry with…"
        disabled={disabled}
        className={styles.messageRetryMenuButton}
        {...buttonProps}
      />
    </ButtonGroup>
  )
}

export function ModelPickerDesign({
  selectedModel,
  models,
  onModelPicked,
  type,
  disabled,
  variant = 'invisible',
  plan,
  showCategories,
  showMultipliers = false,
  showPreviewModelsSeparately = true,
  mode = 'immersive',
}: ModelPickerDesignProps) {
  const [isOpen, setIsOpen] = useState(false)
  const preferredOverlaySide = useOverlayDirection()

  // For free plan users, combine models with hardcoded upsell models, but prioritize API models over duplicates
  const modelsToUse: CopilotChatModel[] =
    plan === CopilotPlan.IndividualFree
      ? (() => {
          // Get display names from API models to check for duplicates
          const apiModelDisplayNames = new Set(models.map(m => m.displayName))
          // Filter out upsell models that have the same display name as API models
          const filteredUpsellModels = upsellModelsForFreeUsers.filter(m => !apiModelDisplayNames.has(m.displayName))
          // Convert upsell models to CopilotChatModel format and combine
          const convertedUpsellModels = filteredUpsellModels.map(createMockModelFromUpsellModel)
          return [...models, ...convertedUpsellModels]
        })()
      : models

  // Use category-based categorization (feature flag removed, assuming always true)
  // When showCategories is explicitly false, disable category grouping
  const shouldShowModelCategories =
    showCategories !== false &&
    copilotFeatureFlags.immersiveStructuredModelPicker &&
    hasModelCategoryDiversity(modelsToUse)

  // Categorize models using the utility function
  const modelTiers = categorizeModels(modelsToUse, {
    shouldShowCategories: shouldShowModelCategories,
    shouldShowPreviewModelsSeparately: showPreviewModelsSeparately,
  })

  // Extract the categorized models for easier access
  const {
    lowTier: lowTierModels,
    midTier: midTierModels,
    highTier: highTierModels,
    base: baseModels,
    preview: previewModels,
    custom: customModelGroups,
  } = modelTiers

  const uniqueVendors = new Set(modelsToUse.map(m => m.vendor))
  const hasMultipleVendors = uniqueVendors.size > 1
  const hasThirdPartyModel = modelsToUse.some(m => m.isThirdParty)
  const hasCustomModels = Object.keys(customModelGroups).length > 0

  const handleMenuOpen = useCallback(
    (open: boolean) => {
      // In case the retry divider is clicked
      const shouldOpen = !disabled && open
      setIsOpen(shouldOpen)

      if (shouldOpen) {
        sendEvent('dotcom_chat.activate', {
          target: 'MODEL_PICKER_OPEN',
          mode: 'immersive',
          model: selectedModel.id,
          type,
        })
      }
    },
    [disabled, selectedModel.id, type],
  )

  const setVariant = variant ?? 'invisible'

  const componentRef = useRef<HTMLDivElement>(null)
  const [menuDirection, setMenuDirection] = useState<'up' | 'down'>('down')

  return (
    <div ref={componentRef}>
      <ActionMenu open={isOpen} onOpenChange={handleMenuOpen}>
        {type === 'global' ? (
          <ActionMenu.Button className={styles.menuButton} variant={setVariant} disabled={disabled}>
            <span className="sr-only">Model: </span>
            <div className={styles.buttonName}>{selectedModel.displayName}</div>
          </ActionMenu.Button>
        ) : (
          <ActionMenu.Anchor aria-label={'Retry with model'}>
            <RetryButtonGroup selectedModel={selectedModel} onModelPicked={onModelPicked} disabled={disabled} />
          </ActionMenu.Anchor>
        )}

        {/* eslint-disable-next-line primer-react/no-system-props */}
        <ActionMenu.Overlay
          width={type === 'global' ? 'medium' : 'small'}
          side={preferredOverlaySide}
          overflow="auto"
          className={styles.actionMenuOverlay}
          maxHeight="fit-content"
          align="end"
          position={mode === 'assistive' ? 'relative' : undefined}
          portalContainerName={mode === 'assistive' ? COPILOT_CHAT_MENU_PORTAL_ROOT : undefined}
          onPositionChange={({position}) => {
            const downPositions = ['inside-bottom', 'outside-bottom']
            setMenuDirection(downPositions.includes(position.anchorSide) ? 'down' : 'up')
          }}
        >
          <ActionList className={styles.actionList} selectionVariant={type === 'global' ? 'single' : undefined}>
            {type === 'message-retry' && menuDirection === 'down' && (
              <>
                <RetryListItem selectedModel={selectedModel} onModelPicked={onModelPicked} />
                <ActionList.Divider />
              </>
            )}
            {type === 'global' && <div className={styles.menuHeading}>Models</div>}

            {baseModels.length > 0 && (
              <ActionList.Group>
                {type !== 'global' && <ActionList.GroupHeading>Models</ActionList.GroupHeading>}
                {baseModels.map(m => (
                  <ModelListItem
                    key={m.id}
                    model={m}
                    selected={isSameModel(selectedModel, m) && type === 'global'}
                    onModelPicked={onModelPicked}
                    hasMultipleVendors={hasMultipleVendors}
                    hasThirdPartyModel={hasThirdPartyModel}
                    showMultipliers={showMultipliers}
                  />
                ))}
                {(lowTierModels.length > 0 ||
                  midTierModels.length > 0 ||
                  highTierModels.length > 0 ||
                  previewModels.length > 0 ||
                  hasCustomModels) && <ActionList.Divider />}
              </ActionList.Group>
            )}

            {lowTierModels.length > 0 && (
              <ActionList.Group>
                <ActionList.GroupHeading>Fast and cost-efficient</ActionList.GroupHeading>

                {lowTierModels.map(m => (
                  <ModelListItem
                    key={m.id}
                    model={m}
                    selected={isSameModel(selectedModel, m) && type === 'global'}
                    onModelPicked={onModelPicked}
                    hasMultipleVendors={hasMultipleVendors}
                    hasThirdPartyModel={hasThirdPartyModel}
                    showMultipliers={showMultipliers}
                  />
                ))}
                {(midTierModels.length > 0 || highTierModels.length > 0) && <ActionList.Divider />}
              </ActionList.Group>
            )}

            {midTierModels.length > 0 && (
              <ActionList.Group>
                <ActionList.GroupHeading>Versatile and highly intelligent</ActionList.GroupHeading>

                {midTierModels.map(m => (
                  <ModelListItem
                    key={m.id}
                    model={m}
                    selected={isSameModel(selectedModel, m) && type === 'global'}
                    onModelPicked={onModelPicked}
                    hasMultipleVendors={hasMultipleVendors}
                    hasThirdPartyModel={hasThirdPartyModel}
                    showMultipliers={showMultipliers}
                  />
                ))}
                {(highTierModels.length > 0 || previewModels.length > 0 || hasCustomModels) && <ActionList.Divider />}
              </ActionList.Group>
            )}

            {highTierModels.length > 0 && (
              <ActionList.Group>
                <ActionList.GroupHeading>Most powerful at complex tasks</ActionList.GroupHeading>

                {highTierModels.map(m => (
                  <ModelListItem
                    key={m.id}
                    model={m}
                    selected={isSameModel(selectedModel, m) && type === 'global'}
                    onModelPicked={onModelPicked}
                    hasMultipleVendors={hasMultipleVendors}
                    hasThirdPartyModel={hasThirdPartyModel}
                    showMultipliers={showMultipliers}
                  />
                ))}
                {(previewModels.length > 0 || hasCustomModels) && <ActionList.Divider />}
              </ActionList.Group>
            )}

            {previewModels.length > 0 && (
              <ActionList.Group>
                <ActionList.GroupHeading>Preview</ActionList.GroupHeading>

                {previewModels.map(m => (
                  <ModelListItem
                    key={m.id}
                    model={m}
                    selected={isSameModel(selectedModel, m) && type === 'global'}
                    onModelPicked={onModelPicked}
                    hasMultipleVendors={hasMultipleVendors}
                    hasThirdPartyModel={hasThirdPartyModel}
                    showMultipliers={showMultipliers}
                  />
                ))}
                {hasCustomModels && <ActionList.Divider />}
              </ActionList.Group>
            )}

            {hasCustomModels && (
              <CustomModelActionListGroups customModelGroups={customModelGroups}>
                {m => (
                  <ModelListItem
                    key={m.id}
                    model={m}
                    selected={isSameModel(selectedModel, m) && type === 'global'}
                    onModelPicked={onModelPicked}
                    hasMultipleVendors={hasMultipleVendors}
                    hasThirdPartyModel={hasThirdPartyModel}
                    showMultipliers={showMultipliers}
                  />
                )}
              </CustomModelActionListGroups>
            )}

            {type === 'message-retry' && menuDirection === 'up' && (
              <>
                <ActionList.Divider />
                <RetryListItem selectedModel={selectedModel} onModelPicked={onModelPicked} />
              </>
            )}
          </ActionList>
          {copilotFeatureFlags.premiumRequestQuotasEnabled && <ModelPickerFooter />}
        </ActionMenu.Overlay>
      </ActionMenu>
    </div>
  )
}

function CustomModelActionListGroups({
  customModelGroups,
  children,
}: {
  customModelGroups: {[groupKey: string]: CustomModelGroup}
  children: (model: CopilotChatModel) => ReactNode
}) {
  const groupKeys = Object.keys(customModelGroups)
  const lastGroupIndex = groupKeys.length - 1

  return groupKeys.map((groupKey, i) => {
    const group = customModelGroups[groupKey]!
    return (
      <ActionList.Group key={groupKey}>
        <ActionList.GroupHeading>{group.displayName}</ActionList.GroupHeading>
        {group.models.map(children)}
        {i < lastGroupIndex && <ActionList.Divider />}
      </ActionList.Group>
    )
  })
}

function ModelPickerFooter() {
  const {
    plan,
    premiumChatQuotaRemaining,
    premiumInteractionsQuotaExceeded,
    chatQuotaRemaining,
    chatQuotaExceeded,
    canPurchaseAdditionalQuota,
    canUpgradePlan,
    overagesEnabled,
    resetDate,
  } = useEntitlement()
  let footerContent = null
  if (plan === CopilotPlan.IndividualFree) {
    if (chatQuotaExceeded) {
      footerContent = (
        <>
          You have used all requests available this month.{' '}
          <Link inline href={UPGRADE_PLAN_URL}>
            Upgrade
          </Link>
          .
        </>
      )
    } else if (chatQuotaRemaining <= FREE_QUOTA_TRIGGER) {
      footerContent = (
        <>
          You have used {100 - FREE_QUOTA_TRIGGER}% of your free requests this month.{' '}
          <Link inline href={UPGRADE_PLAN_URL}>
            Upgrade
          </Link>{' '}
          to increase your limit.
        </>
      )
    } else {
      footerContent = (
        <>
          <Link inline href={UPGRADE_PLAN_URL}>
            Upgrade
          </Link>{' '}
          to access more models and higher limits.
        </>
      )
    }
  }
  if (plan === CopilotPlan.IndividualPro) {
    if (premiumInteractionsQuotaExceeded && overagesEnabled) {
      footerContent = (
        <>
          You have used all premium requests available this month. You will be charged per premium request until the
          limit resets on {resetDate}.{' '}
          <Link inline href={MANAGE_BILLING_URL}>
            Manage billing
          </Link>
        </>
      )
    } else if (premiumInteractionsQuotaExceeded && !overagesEnabled) {
      footerContent = (
        <>
          You have used all premium requests available this month.{' '}
          <Link inline href={UPGRADE_PLAN_URL}>
            Upgrade
          </Link>{' '}
          to increase your limit. Limit resets on {resetDate}.
        </>
      )
    } else if (premiumChatQuotaRemaining <= PREMIUM_QUOTA_TRIGGER && !overagesEnabled) {
      footerContent = (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <Link inline href={UPGRADE_PLAN_URL}>
            Upgrade
          </Link>{' '}
          to increase your limit.
        </>
      )
    }
  }
  if (plan === CopilotPlan.IndividualProPlus) {
    if (premiumInteractionsQuotaExceeded && overagesEnabled) {
      footerContent = (
        <>
          You have used all premium requests available this month. You will be charged per premium request until the
          limit resets on {resetDate}.{' '}
          <Link inline href={MANAGE_BILLING_URL}>
            Manage billing
          </Link>
        </>
      )
    } else if (premiumInteractionsQuotaExceeded && !overagesEnabled) {
      footerContent = (
        <>
          You have used all premium requests available this month.{' '}
          <Link inline href={ENABLE_ADDITIONAL_REQUESTS_URL}>
            Enable additional requests
          </Link>{' '}
          to get more. Limit resets on {resetDate}.
        </>
      )
    } else if (premiumChatQuotaRemaining <= PREMIUM_QUOTA_TRIGGER && !overagesEnabled) {
      footerContent = (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <Link inline href={ENABLE_ADDITIONAL_REQUESTS_URL}>
            Enable additional requests
          </Link>{' '}
          to get more.
        </>
      )
    }
  }
  if (plan === CopilotPlan.Business) {
    if (premiumInteractionsQuotaExceeded && !overagesEnabled && canUpgradePlan) {
      footerContent = (
        <>
          You have used all premium requests available this month.{' '}
          <Link inline href={UPGRADE_PLAN_URL}>
            Upgrade
          </Link>{' '}
          to increase your limit. Limit resets on {resetDate}.
        </>
      )
    } else if (premiumChatQuotaRemaining <= PREMIUM_QUOTA_TRIGGER && !overagesEnabled && canUpgradePlan) {
      footerContent = (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <Link inline href={UPGRADE_PLAN_URL}>
            Upgrade
          </Link>{' '}
          to increase your limit.
        </>
      )
    } else if (premiumInteractionsQuotaExceeded && !overagesEnabled && !canUpgradePlan) {
      footerContent = (
        <>
          You have reached your monthly limit for premium requests.{' '}
          <span className="font-weight-bold">Ask your admin to upgrade </span>
          to increase your limit. Limit resets on {resetDate}.
        </>
      )
    } else if (premiumChatQuotaRemaining <= PREMIUM_QUOTA_TRIGGER && !overagesEnabled && !canUpgradePlan) {
      footerContent = (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <span className="font-weight-bold">Ask your admin to upgrade </span>
          to increase your limit.
        </>
      )
    }
  }
  if (plan === CopilotPlan.Enterprise) {
    if (premiumInteractionsQuotaExceeded && !overagesEnabled && canPurchaseAdditionalQuota) {
      footerContent = (
        <>
          You have used all premium requests available this month.{' '}
          <Link inline href={ENABLE_ADDITIONAL_REQUESTS_URL}>
            Enable additional requests
          </Link>{' '}
          to increase your limit. Limit resets on {resetDate}.
        </>
      )
    } else if (premiumInteractionsQuotaExceeded && !overagesEnabled && !canPurchaseAdditionalQuota) {
      footerContent = (
        <>
          You have reached your monthly limit for premium requests. Ask your admin to{' '}
          <span className="font-weight-bold">enable additional requests </span>
          to get more usage. Limit resets on {resetDate}.
        </>
      )
    } else if (premiumChatQuotaRemaining <= PREMIUM_QUOTA_TRIGGER && !overagesEnabled && canPurchaseAdditionalQuota) {
      footerContent = (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month.{' '}
          <Link inline href={ENABLE_ADDITIONAL_REQUESTS_URL}>
            Enable additional requests
          </Link>{' '}
          to get more usage after the limit is reached.
        </>
      )
    } else if (premiumChatQuotaRemaining <= PREMIUM_QUOTA_TRIGGER && !overagesEnabled && !canPurchaseAdditionalQuota) {
      footerContent = (
        <>
          You have used {100 - PREMIUM_QUOTA_TRIGGER}% of your premium requests this month. Ask your admin to{' '}
          <span className="font-weight-bold">enable additional requests </span>
          to get more usage.
        </>
      )
    }
  }

  return footerContent ? <div className={styles.footer}>{footerContent}</div> : null
}

interface RetryListItemProps {
  selectedModel: CopilotChatModel
  onModelPicked: (m: CopilotChatModel) => void
}

function RetryListItem({selectedModel, onModelPicked}: RetryListItemProps) {
  return (
    <ActionList.Item key={selectedModel.id} onSelect={() => onModelPicked(selectedModel)}>
      <ActionList.LeadingVisual>
        <SyncIcon />
      </ActionList.LeadingVisual>
      Try again
      <ActionList.Description variant="block" truncate>
        {selectedModel.displayName}
      </ActionList.Description>
    </ActionList.Item>
  )
}

interface ModelListItemProps {
  model: CopilotChatModel
  selected: boolean
  onModelPicked: (m: CopilotChatModel) => void
  hasMultipleVendors: boolean
  hasThirdPartyModel: boolean
  showMultipliers?: boolean
}

export function ModelListItem({
  model,
  selected,
  onModelPicked,
  hasMultipleVendors,
  hasThirdPartyModel,
  showMultipliers = false,
}: ModelListItemProps) {
  const {plan, premiumInteractionsQuotaExceeded, chatQuotaExceeded, overagesEnabled} = useEntitlement()
  const freePlanAndOutOfQuota = plan === CopilotPlan.IndividualFree && chatQuotaExceeded
  const premiumModelAndOutOfQuota =
    model.billing?.is_premium &&
    plan !== CopilotPlan.IndividualFree &&
    premiumInteractionsQuotaExceeded &&
    !overagesEnabled
  const modelOutOfPlan = plan && model.billing?.restricted_to && !model.billing?.restricted_to.includes(plan)
  const isUpsellModel = 'upsellModel' in model && model.upsellModel === true
  const locked = freePlanAndOutOfQuota || premiumModelAndOutOfQuota || modelOutOfPlan || isUpsellModel

  const multiplierText = showMultipliers && !isCustomModel(model) ? formatMultiplier(model.billing?.multiplier) : null

  return (
    <ActionList.Item key={model.id} onSelect={() => onModelPicked(model)} selected={selected} disabled={!!locked}>
      {(hasMultipleVendors || hasThirdPartyModel) && (
        <ActionList.LeadingVisual>
          <ModelVendorIcon model={model} />
        </ActionList.LeadingVisual>
      )}
      <span className={clsx(locked && styles.modelNameDisabled)}>{model.displayName}</span>
      {copilotFeatureFlags.premiumRequestQuotasEnabled && model.preview && model.id !== BASE_MODEL_ID && (
        <span className={styles.modelMetaLabel}>Preview</span>
      )}
      {!copilotFeatureFlags.premiumRequestQuotasEnabled && (hasMultipleVendors || hasThirdPartyModel) && (
        <ActionList.Description variant="block" truncate>
          {model.vendor}
        </ActionList.Description>
      )}
      {multiplierText && (
        <ActionList.TrailingVisual>
          <span className={styles.multiplier}>{multiplierText}</span>
        </ActionList.TrailingVisual>
      )}
    </ActionList.Item>
  )
}

function NewConversationConfirmationDialog({
  model,
  onDismiss,
  onSwitch,
  reason,
}: {
  model: CopilotChatModel
  onDismiss: () => void
  onSwitch: () => void
  reason: string
}) {
  return (
    <ConfirmationDialog
      title="Switch model"
      onClose={gesture => (gesture === 'confirm' ? onSwitch() : onDismiss())}
      confirmButtonContent="New conversation"
      confirmButtonType="primary"
    >
      {reason === 'external-content' && (
        <p>
          The {model.displayName} model currently doesn’t support external context from ongoing conversations. Start a
          new conversation to chat with this model.
        </p>
      )}
      {reason === 'degraded-image-support' && (
        <p>
          The {model.displayName} model doesn’t support answering questions about images. Start a new conversation
          without images to chat with this model.
        </p>
      )}
    </ConfirmationDialog>
  )
}

function ModelVendorIcon({model}: {model: CopilotChatModel}) {
  if (isCustomModel(model) && model.custom_model.provider) {
    return <ProviderLogo provider={model.custom_model.provider} />
  }

  return model.logoURL ? (
    <GitHubAvatar alt={`${model.vendor} logo`} square size={18} src={model.logoURL} />
  ) : (
    <CopilotIcon />
  )
}

function modelSwitchingRequiresNewConversationDueToExternalContent(
  messages: readonly CopilotChatMessage[],
  model: CopilotChatModel,
  onlyActiveMessages?: boolean,
): boolean {
  const messagesToCheck = onlyActiveMessages ? getActiveMessages(messages) : messages
  const hasFunctionCalls = messagesToCheck.some(message => message.skillExecutions?.length)

  return (
    hasFunctionCalls &&
    (!model.capabilities?.supports?.tool_calls ||
      model.isThirdParty ||
      // TODO: temporary - o1-ga supports tool calling, but the dotcom chat code in CAPI currently does not
      // As of 2024-12-17 we expect this to be done in a few business days
      model.capabilities.family === 'o1-ga')
  )
}

function modelSwitchingRequiresNewConversationDueToDegradedImageSupport(
  messages: readonly CopilotChatMessage[],
  currentReferences: CopilotChatReference[],
  model: CopilotChatModel,
  onlyActiveMessages?: boolean,
): boolean {
  const messagesToCheck = onlyActiveMessages ? getActiveMessages(messages) : messages
  const hasMediaContent =
    messagesToCheck.some(message => message.mediaContent?.length) || currentReferences.some(cr => cr.type === 'image')

  return hasMediaContent && !model.capabilities?.supports?.vision
}

function isSameModel(oldModel: CopilotChatModel, newModel: CopilotChatModel): boolean {
  return oldModel.id === newModel.id
}

type UpsellModel = Pick<CopilotChatModel, 'logoURL' | 'displayName' | 'id' | 'model_picker_category'> & {
  upsellModel: boolean
  billing: {multiplier: number}
  preview?: boolean
}

type UpsellModelList = UpsellModel[]

// Helper function to create a mock CopilotChatModel from UpsellModel for type compatibility
function createMockModelFromUpsellModel(upsellModel: UpsellModel): CopilotChatModel {
  const vendor = upsellModel.logoURL?.includes('anthropic')
    ? 'Anthropic'
    : upsellModel.logoURL?.includes('openai')
      ? 'Azure OpenAI'
      : upsellModel.logoURL?.includes('gemini')
        ? 'Google'
        : upsellModel.logoURL?.includes('x-ai')
          ? 'xAI'
          : 'Unknown'

  return {
    ...upsellModel,
    name: upsellModel.displayName,
    version: '1.0',
    vendor,
    // eslint-disable-next-line camelcase
    model_picker_enabled: true,
    hasLimitedCapabilities: false,
    isThirdParty: vendor !== 'Azure OpenAI',
    capabilities: {
      family: 'unknown',
      limits: {
        // eslint-disable-next-line camelcase
        max_prompt_tokens: 4096,
      },
      supports: {},
      tokenizer: 'gpt',
      type: 'chat' as const,
    },
    preview: upsellModel.preview ?? false,
    billing: {
      ...upsellModel.billing,
      // eslint-disable-next-line camelcase
      is_premium: true,
    },
  } as CopilotChatModel
}

/**
 * Hardcoded list of upsell models displayed to free plan users in the model picker.
 *
 * **Why this list exists:**
 * The `/models` endpoint only returns models that are accessible to a given user based on their
 * entitlements. We do not currently have a way to receive all available models from CAPI,
 * including premium/upsell models that free users cannot access but should see as upgrade options.
 *
 * **Why we can't easily change this in CAPI:**
 * The `/models` endpoint serves various integrators (IDE extensions, CLI, etc.) who rely on it
 * returning only actionable models. Changing this behavior would require coordinated updates
 * across all integrators and could break existing functionality.
 *
 * @see {@link createMockModelFromUpsellModel} for how these are converted to CopilotChatModel
 */
const upsellModelsForFreeUsers: UpsellModelList = [
  // Lightweight category
  {
    id: 'grok-code-upgrade',
    displayName: 'Grok Code Fast 1',
    logoURL: '/images/modules/marketplace/models/families/xai.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'lightweight',
    billing: {
      multiplier: 0.33,
    },
  },
  {
    id: 'gemini-3-flash-upgrade',
    displayName: 'Gemini 3 Flash',
    logoURL: '/images/modules/marketplace/models/families/gemini.svg',
    upsellModel: true,
    preview: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'lightweight',
    billing: {
      multiplier: 0.33,
    },
  },
  // Versatile category
  {
    id: 'gpt-5-upgrade',
    displayName: 'GPT-5',
    logoURL: '/images/modules/marketplace/models/families/openai-logo.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'versatile',
    billing: {
      multiplier: 1,
    },
  },
  {
    id: 'gpt-5.1-upgrade',
    displayName: 'GPT-5.1',
    logoURL: '/images/modules/marketplace/models/families/openai-logo.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'versatile',
    billing: {
      multiplier: 1,
    },
  },
  {
    id: 'gpt-5.2-upgrade',
    displayName: 'GPT-5.2',
    logoURL: '/images/modules/marketplace/models/families/openai-logo.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'versatile',
    billing: {
      multiplier: 1,
    },
  },
  {
    id: 'claude-sonnet-4-upgrade',
    displayName: 'Claude Sonnet 4',
    logoURL: '/images/modules/marketplace/models/families/anthropic.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'versatile',
    billing: {
      multiplier: 1,
    },
  },
  {
    id: 'claude-sonnet-4.5-upgrade',
    displayName: 'Claude Sonnet 4.5',
    logoURL: '/images/modules/marketplace/models/families/anthropic.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'versatile',
    billing: {
      multiplier: 1,
    },
  },
  // Powerful category
  {
    id: 'claude-opus-4.5-upgrade',
    displayName: 'Claude Opus 4.5',
    logoURL: '/images/modules/marketplace/models/families/anthropic.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'powerful',
    billing: {
      multiplier: 10,
    },
  },
  {
    id: 'gemini-3-pro-upgrade',
    displayName: 'Gemini 3 Pro',
    logoURL: '/images/modules/marketplace/models/families/gemini.svg',
    upsellModel: true,
    // eslint-disable-next-line camelcase
    model_picker_category: 'powerful',
    billing: {
      multiplier: 10,
    },
    preview: true,
  },
]
